import type { CollectibleId } from "@/services/collectibles";
import { type CollectionItemId } from "@/services/collection/item";
import type { CurrencyId } from "@/services/currencies";
import type { AvailableMarketplaceItemShipping_Schema, MarketplaceItem_Schema, MarketplaceItemElastic_Schema, MarketplaceItemId_Schema } from "@/services/marketplace/item/marketplaceItemSchema";
import type { ShippingMethodId } from "@/services/marketplace/shippingMethod";
import { type PriceNumeric } from "@/services/prices";
import type { UserId } from "@/services/user";
import type { z } from "zod";
import type { MarketplaceItemBasics_Schema } from "./marketplaceItemBasicsSchema";

export enum Sort {
  PriceAsc = "priceMinToMax",
  PriceDesc = "priceMaxToMin",
  DateDesc = "marketplaceListedNewestFirst",
  DateAsc = "marketplaceListedOldestFirst",
  NameAsc = "alphabetAsc",
  NameDesc = "alphabetDesc",
  Likes = "likesCountDesc",
  LikesRecent = "recentLikesCountDesc",
  Relevance = "relevance",
}

export type MarketplaceItemId = z.infer<typeof MarketplaceItemId_Schema>;
export type MarketplaceItem = z.infer<typeof MarketplaceItem_Schema>;
export type MarketplaceItemElastic = z.infer<typeof MarketplaceItemElastic_Schema>;

export type AvailableMarketplaceItemShipping = z.infer<typeof AvailableMarketplaceItemShipping_Schema>;

export type MarketplaceItem_CreatedFromCollectible = MarketplaceItem & {
  collectible: {
    id: CollectibleId;
    name: string;
  };
};

export type MarketplaceItem_Api_UpdateData = {
  quantity: number;
  note: string;
  itemPrice: {
    id: number;
    priceAmountCents: number;
    priceCurrency: CurrencyId;
  };
  itemDelivery: {
    id: number;
    delivery: ShippingMethodId;
    numberPerDelivery: number;
  }[];
  collectionItemId: CollectionItemId;
};

export type MarketplaceItem_Api_CreationData = {
  quantity: number;
  note: string;
  itemPrice: {
    priceAmountCents: number;
    priceCurrency: CurrencyId;
  };
  itemDelivery: {
    delivery: ShippingMethodId;
    numberPerDelivery: number;
  }[];
  collectionItemId: CollectionItemId;
};

export type MarketplaceItem_FormModel = {
  name: string | undefined;
  quantity: number | undefined;
  description: string | undefined;
  price: PriceNumeric | undefined;
};

export type MarketplaceItem_ShippingSetup = Map<ShippingMethodId, number>;

export class MarketplaceItemNotFoundException extends Error {}

export type OfferPriceConfig = {
  ownerId: UserId;
  itemId: MarketplaceItemId | CollectionItemId;
};

export type MarketplaceItemBasicInfo = z.infer<typeof MarketplaceItemBasics_Schema>;
